/**
 * Viewport prefetch.
 *
 * Warms the prefetch cache for internal links as they enter the
 * viewport, so the first click on a not-yet-visited page finds its
 * SPA fetch already resolved (or in flight) instead of paying the
 * full server render at click time. SPA envelope responses bypass
 * Drupal's page cache by design, which makes that first fetch the
 * slowest part of a navigation; the hover-intent prefetch only
 * helps pointer users who linger on a link, and never helps touch.
 *
 * Mechanics:
 * - An IntersectionObserver (200px root margin) watches every
 *   navigable, prefetchable link. A link is unobserved the moment
 *   it intersects and its URL joins a paced queue.
 * - The queue dispatches one prefetch every PACE_MS instead of
 *   bursting: a content page can expose dozens of internal links,
 *   and each prefetch is an uncached server render.
 * - The link scan runs on install and again after every completed
 *   SPA navigation (NAV_EVENT.END), picking up swapped-in content.
 * - Respects `Save-Data` and 2g connections by not installing, and
 *   is a no-op where IntersectionObserver is unavailable.
 * - The controller's prefetch() already refuses duplicates (cache
 *   hits and the URL currently in flight), so the queue only
 *   dedupes what it has itself enqueued.
 *
 * Enabled by default; sites opt out with
 * `drupalSettings.sdx.navigate.viewportPrefetch = false`.
 */

import { isNavigable, isPrefetchable, resolveNavLink, type NavigableRules } from './url';
import { NAV_EVENT } from './types';
import type { NavigationController } from './navigation-controller';

const ROOT_MARGIN = '200px';
const PACE_MS = 300;

export interface ViewportPrefetchContext {
  readonly controller: Pick<NavigationController, 'prefetch'>;
  readonly rules: NavigableRules;
}

interface ConnectionInfo {
  saveData?: boolean;
  effectiveType?: string;
}

function isConstrainedConnection(): boolean {
  const connection = (navigator as { connection?: ConnectionInfo }).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return /(^|-)2g$/.test(connection.effectiveType ?? '');
}

/**
 * Installs viewport prefetching. Returns an uninstall callback for
 * teardown symmetry with the other listeners.
 */
export function installViewportPrefetch(ctx: ViewportPrefetchContext): () => void {
  if (typeof IntersectionObserver === 'undefined' || isConstrainedConnection()) {
    return () => {};
  }

  const observed = new WeakSet<Element>();
  const queued = new Set<string>();
  const queue: string[] = [];
  let paceTimer: ReturnType<typeof setTimeout> | null = null;

  const drain = (): void => {
    if (paceTimer !== null) return;
    const next = queue.shift();
    if (next === undefined) return;
    ctx.controller.prefetch(next);
    paceTimer = setTimeout(() => {
      paceTimer = null;
      drain();
    }, PACE_MS);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        const link = resolveNavLink(entry.target);
        if (!link || queued.has(link.href)) continue;
        queued.add(link.href);
        queue.push(link.href);
      }
      drain();
    },
    { rootMargin: ROOT_MARGIN },
  );

  const scan = (): void => {
    for (const anchor of document.querySelectorAll('a[href]')) {
      if (observed.has(anchor)) continue;
      const link = resolveNavLink(anchor);
      if (!link || !isNavigable(link, ctx.rules) || !isPrefetchable(link)) continue;
      if (queued.has(link.href)) continue;
      observed.add(anchor);
      observer.observe(anchor);
    }
  };

  const onNavigationEnd = (): void => scan();

  scan();
  document.addEventListener(NAV_EVENT.END, onNavigationEnd);

  return () => {
    document.removeEventListener(NAV_EVENT.END, onNavigationEnd);
    observer.disconnect();
    if (paceTimer !== null) {
      clearTimeout(paceTimer);
      paceTimer = null;
    }
    queue.length = 0;
  };
}
